"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { ArrowRight, Calendar, Search, CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
export default function FinalCta() {
  const shouldReduceMotion = useReducedMotion();
  const benefits = ["Strategy and execution handled by one consultant", "Tailored UK and international search strategy", "Direct communication with one consultant throughout"];
  return <section id="finalctabanner" data-nav="light" className="relative overflow-hidden bg-background py-20 md:py-28">
      {/* Subtle decorative background glow */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 flex items-center justify-center opacity-40">
        <div className="size-[500px] rounded-full bg-secondary/60 blur-3xl md:size-[700px]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.3
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="relative rounded-3xl border border-border bg-card/80 p-8 sm:p-12 md:p-16 text-center shadow-lg backdrop-blur-xs">
          {/* Subtle Top Badge */}
          <div className="mb-6 flex justify-center">
            <Badge variant="secondary" className="gap-1.5 px-3.5 py-1 text-xs font-medium text-foreground">
              <SafeHydrate><Calendar className="size-3.5 text-primary" /></SafeHydrate>
              Book a Consultation
            </Badge>
          </div>

          {/* Heading */}
          <h2 className="mx-auto max-w-3xl font-serif text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Ready to discuss your SEO requirements?
          </h2>

          {/* Body Copy with Primary Keyword */}
          <p className="mx-auto mt-6 max-w-2xl font-sans text-base leading-relaxed text-muted-foreground sm:text-lg">
            As a hands-on <span className="font-semibold text-foreground">SEO agency alternative in the UK</span>, I work directly on your search strategy from planning through to execution. Let’s look at your current rankings and map out a practical roadmap for sustainable growth.
          </p>

          {/* Benefits Checkpoints */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-foreground/90">
            {benefits.map((benefit, i) => <div key={benefit} data-index={i} className="inline-flex items-center gap-2 font-medium">
                <CheckCircle2 className="size-4 text-primary shrink-0" />
                <span>{benefit}</span>
              </div>)}
          </div>

          {/* Action Buttons */}
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-semibold shadow-md transition-[color,background-color,box-shadow]">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                <SafeHydrate><Calendar className="size-4" /></SafeHydrate>
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto h-12 px-8 text-base font-medium transition-[color,background-color,box-shadow] hover:bg-accent hover:text-accent-foreground">
              <Link to="/SeoAuditsPage" className="inline-flex items-center gap-2">
                <Search className="size-4" />
                <span>Get a Free SEO Audit</span>
              </Link>
            </Button>
          </div>

          {/* Guarantee / Clarification Note */}
          <p className="mt-6 text-xs text-muted-foreground">
            No obligation, no sales pitch — just practical, actionable insights for your business.
          </p>
        </motion.div>
      </div>
    </section>;
}